import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';

export default function CartPage() {
  const { cartItems, cartTotal, updateQuantity, removeItem, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!user) { navigate('/auth'); return; }
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div data-testid="empty-cart" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground/30 mb-6" />
        <h2 className="text-2xl font-serif font-medium mb-2">Your cart is empty</h2>
        <p className="text-muted-foreground mb-8">Explore our fresh produce from local farmers.</p>
        <Link to="/products">
          <Button data-testid="browse-products-button" className="rounded-full px-8">Browse Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-serif font-medium mb-8">Your Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {cartItems.map((item) => (
            <div key={item.item_id} data-testid={`cart-item-${item.item_id}`}
              className="bg-card rounded-3xl border border-border/50 p-5 flex gap-4 shadow-sm">
              <img src={item.image} alt={item.product_name} className="w-20 h-20 rounded-2xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-serif font-medium text-base">{item.product_name}</h3>
                <p className="text-xs text-muted-foreground">{item.farmer_name} &middot; {item.variant_name} ({item.variant_weight})</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="inline-flex items-center gap-3 bg-muted rounded-full p-0.5">
                    <button data-testid={`cart-decrease-${item.item_id}`} onClick={() => updateQuantity(item.item_id, Math.max(1, item.quantity - 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-background transition-colors">
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="text-sm font-semibold w-4 text-center">{item.quantity}</span>
                    <button data-testid={`cart-increase-${item.item_id}`} onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-background transition-colors">
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-semibold">{'\u20B9'}{item.price * item.quantity}</span>
                    <button data-testid={`cart-remove-${item.item_id}`} onClick={() => { removeItem(item.item_id); toast.success('Removed from cart'); }}
                      className="p-1.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button data-testid="clear-cart-button" onClick={() => { clearCart(); toast.success('Cart cleared'); }}
            className="text-sm text-muted-foreground hover:text-destructive transition-colors">Clear Cart</button>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-3xl border border-border/50 p-6 sticky top-24 shadow-sm">
            <h3 className="font-serif font-medium text-lg mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{'\u20B9'}{cartTotal}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="text-primary font-medium">Free</span></div>
            </div>
            <Separator className="my-4" />
            <div className="flex justify-between text-lg font-semibold mb-6">
              <span>Total</span><span data-testid="cart-total">{'\u20B9'}{cartTotal}</span>
            </div>
            <Button data-testid="proceed-to-checkout-button" onClick={handleCheckout} className="w-full rounded-full" size="lg">
              Proceed to Checkout <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
